/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
/* tslint:disable: no-duplicate-imports */
import { getTelemetryObject, IModuleProps, INodeProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import { getRichTextHtml } from '@msdyn365-commerce/core';
import { computed } from 'mobx';
import * as Msdyn365 from '@msdyn365-commerce/core';
import classnames from 'classnames';
import * as React from 'react';
import LinksComponent from './components/links';
import { isValidColour } from '../../common/method/functions';
import { actionableRegion as region, IFarmlandsCarouselContentBlockProps } from './farmlands-carousel-content-block.props.autogenerated';

type DeviceType = 'Mobile';
export interface IContentBlockViewProps extends IFarmlandsCarouselContentBlockProps<{}> {
    title?: React.ReactNode;
    text?: React.ReactNode;
    image?: React.ReactNode;
    links?: React.ReactNode;
    className?: string;
    contentBlockContainer: IModuleProps;
    imageContainer: INodeProps;
    detailsDesktopContainer: INodeProps;
    detailsMobileContainer: INodeProps;
    contentBlockAnchorTag?: INodeProps;
    imageLink?: string;
    shouldOpenLinkInNewTab?: boolean;
    imageAriaLabel?: string;
}
/**
 *
 * ContentCard component
 * @extends {React.PureComponent<IFarmlandsCarouselContentBlockProps{}>}
 */

class ContentBlock extends React.PureComponent<IFarmlandsCarouselContentBlockProps<{}>> {
    private telemetryContent: ITelemetryContent = getTelemetryObject(
        this.props.context.request.telemetryPageName!,
        this.props.friendlyName,
        this.props.telemetry
    );

    @computed get isMobile(): boolean {
        return (
            this.props.context.request &&
            this.props.context.request.device &&
            (this.props.context.request.device.Type as DeviceType) === 'Mobile'
        );
    }

    public render(): JSX.Element | null {
        const {
            heading,
            mobileHeadingColour,
            desktopHeadingColour,
            paragraph,
            mobileParagraphColour,
            desktopParagraphColour,
            mobileParagraphBackgroundColour,
            desktopImage,
            mobileImage,
            links,
            shouldOpenLinkInNewTab,
            className,
            imageAriaLabel
        } = this.props.config;

        const CustomTag = (`${heading?.tag}` || 'h1') as keyof JSX.IntrinsicElements;
        const contentBlockTitle = heading && (
            <CustomTag
                className='farmlands-carousel-content-block__title'
                style={
                    this.isMobile
                        ? {
                              color: isValidColour(mobileHeadingColour) || '#ffffff'
                          }
                        : {
                              color: isValidColour(desktopHeadingColour) || '#000000'
                          }
                }
            >
                {heading.text}
            </CustomTag>
        );
        const imageProps = (imageType: Msdyn365.IImageData) => {
            return {
                gridSettings: this.props.context.request.gridSettings || {},
                imageSettings: imageType && imageType.imageSettings
            };
        };
        const contentBlockLinks = links && links.length > 0 && (
            <LinksComponent
                {...{
                    links: links,
                    onTextChange: this.handleLinkTextChange,
                    requestContext: this.props.context.request,
                    telemetryContent: this.telemetryContent
                }}
            />
        );
        const contentBlockText = paragraph && (
            // tslint:disable-next-line: react-no-dangerous-html
            <div
                className='farmlands-carousel-content-block__text'
                style={
                    this.isMobile
                        ? {
                              color: isValidColour(mobileParagraphColour) || '#ffffff'
                          }
                        : {
                              color: isValidColour(desktopParagraphColour) || '#000000'
                          }
                }
                dangerouslySetInnerHTML={getRichTextHtml(paragraph)}
            />
        );
        const contentBlockImage = desktopImage && (
            <>
                <Msdyn365.Image
                    className='msc_image--desktop'
                    {...desktopImage}
                    {...imageProps(desktopImage)}
                    editProps={{
                        key: this.props.config.desktopImage || {},
                        requestContext: this.props.context.request
                    }}
                />
                {mobileImage && (
                    <Msdyn365.Image
                        className='msc_image--mobile'
                        {...mobileImage}
                        {...imageProps(mobileImage)}
                        editProps={{
                            key: this.props.config.mobileImage || {},
                            requestContext: this.props.context.request
                        }}
                    />
                )}
            </>
        );

        if (!contentBlockTitle && !contentBlockText && !contentBlockImage && !contentBlockLinks) {
            this.props.context.telemetry.error('Content block content is empty, module wont render.');
            return null;
        }

        const contentBlockviewProps = {
            ...this.props,
            title: contentBlockTitle,
            text: contentBlockText,
            image: contentBlockImage,
            links: contentBlockLinks,
            moduleClass: this.props.config.className,
            contentBlockContainer: {
                moduleProps: this.props,
                className: classnames('farmlands-carousel-content-block', className)
            },
            imageContainer: { className: 'farmlands-carousel-content-block__image' },
            detailsDesktopContainer: {
                className: 'farmlands-carousel-content-block__details farmlands-carousel-content-block__details--desktop'
            },
            detailsMobileContainer: {
                style: { 'background-color': isValidColour(mobileParagraphBackgroundColour) || '#000000' },
                className: 'farmlands-carousel-content-block__details farmlands-carousel-content-block__details--mobile'
            },
            contentBlockAnchorTag: {
                tag: 'a',
                className: 'farmlands-carousel-content-block__link',
                role: 'link'
            },
            imageLink: this._getImageLink(),
            imageAriaLabel,
            shouldOpenLinkInNewTab: shouldOpenLinkInNewTab
        };

        return this.props.renderView(contentBlockviewProps) as React.ReactElement;
    }
    public handleTextChange = (event: Msdyn365.ContentEditableEvent) => (this.props.config.heading!.text = event.target.value);
    public handleParagraphChange = (event: Msdyn365.ContentEditableEvent) => (this.props.config.paragraph = event.target.value);
    /* Handle link text change*/
    public handleLinkTextChange = (linkIndex: number) => (event: Msdyn365.ContentEditableEvent) => {
        if (this.props.config.links && this.props.config.links[linkIndex]) {
            this.props.config.links[linkIndex].linkText = event.target.value;
        }
    };
    private _getImageLink(): string | null {
        const { imageLink, links, actionableRegion } = this.props.config;
        if (actionableRegion === region.imageAndLinks) {
            if (imageLink && imageLink.destinationUrl) {
                return imageLink.destinationUrl;
            } else if (links && links.length && links[0].linkUrl) {
                return links[0].linkUrl.destinationUrl;
            } else {
                return null;
            }
        } else {
            return null;
        }
    }
}

export default ContentBlock;
